// #1515 — the shared mechanics of "who attended this training record", not a
// grand unified definition of "a training".
//
// The department already counts trainings several defensible ways: the practice
// screens dedupe by day (two drills on one evening are one practice night), the
// payroll screens count hours, and the Member Performance Review report counts
// every record individually, grouped by type. Those differ on purpose. What they
// must NOT differ on is the boring part — who is on a record, and which type
// bucket the record belongs to — so that lives here.
//
// Pure module, same house style as memberStatus.js: no firebase, no moment,
// duck-typed inputs. `members` / `instructors` may hold uid strings or populated
// user objects depending on whether the caller ran populateUsers().

// Attendance = on the record, as a member OR as an instructor. Instructing
// counts as attending — the existing convention across the training screens (an
// officer who ran the drill was there).
const trainingAttendeeUids = training => {
    const {members = [], instructors = []} = training || {};

    const toUid = entry => {
        if (!entry) return null;
        if (typeof entry === 'string') return entry;
        return entry.uid || null;
    };

    const seen = new Set();
    [...members, ...instructors].forEach(entry => {
        const uid = toUid(entry);
        if (uid) seen.add(uid);
    });

    return [...seen];
};

// Was this member on the record?
const attendedTraining = (training, uid) => !!uid && trainingAttendeeUids(training).includes(uid);

// Canonical type key for a training doc. Types are stored as the uppercase keys
// of TrainingTypes, but `COURSE_INTERNAL` predates the rename to
// `INTERNAL_COURSE` and still sits on old docs (the practice screens have
// special-cased it for years — see pages/trainings/stats/Attendance.js).
// Anything unrecognizable buckets as UNKNOWN rather than vanishing.
const TYPE_ALIASES = {
    COURSE_INTERNAL: 'INTERNAL_COURSE',
    COURSE_EXTERNAL: 'EXTERNAL_COURSE'
};

const normalizeTrainingType = type => {
    const normalized = (type === null || type === undefined ? '' : String(type)).trim().toUpperCase();
    if (!normalized) return 'UNKNOWN';
    return TYPE_ALIASES[normalized] || normalized;
};

// `{[normalizedType]: [training]}`, input order preserved within each bucket.
const groupTrainingsByType = trainings => {
    const groups = {};

    (trainings || []).forEach(training => {
        if (!training) return;
        const type = normalizeTrainingType(training.type);
        (groups[type] = groups[type] || []).push(training);
    });

    return groups;
};

module.exports = {
    trainingAttendeeUids,
    attendedTraining,
    normalizeTrainingType,
    groupTrainingsByType
};
